import { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, Button, Tag, Input } from 'antd';
import { DownloadOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = import.meta.env.VITE_API_BASE || '/api';
const h = () => ({ Authorization: 'Bearer ' + localStorage.getItem('ar_token') });

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dealerFilter, setDealerFilter] = useState('');

  const fetchData = () => {
    setLoading(true);
    axios.get(API + '/arrival/admin-dashboard', { headers: h() }).then(r => {
      if (r.data.success) setData(r.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleExport = (status) => {
    const url = API + '/arrival/export' + (status ? `?sign_status=${status}` : '');
    window.open(url);
  };

  const { kpi, dealers, months, sign_types } = data || {};
  const safeDealers = dealers || [];
  const safeMonths = months || [];
  const safeSignTypes = sign_types || [];
  const safeKpi = kpi || {};

  const filteredDealers = useMemo(() => {
    if (!dealerFilter) return safeDealers;
    const kw = dealerFilter.toLowerCase();
    return safeDealers.filter(d => d.name.toLowerCase().includes(kw));
  }, [safeDealers, dealerFilter]);

  const dealerCols = [
    { title: '经销商', dataIndex: 'name', width: 200, ellipsis: true },
    { title: '总数', dataIndex: 'total', width: 80, sorter: (a, b) => a.total - b.total },
    { title: '已签', dataIndex: 'signed', width: 80, sorter: (a, b) => a.signed - b.signed,
      render: (v, r) => <span style={{ color: '#52c41a' }}>{v}</span> },
    { title: '未签', dataIndex: 'unsigned', width: 80, sorter: (a, b) => a.unsigned - b.unsigned,
      render: (v, r) => <span style={{ color: v > 0 ? '#ff4d4f' : '#999' }}>{v}</span> },
    { title: '签收率', dataIndex: 'total', width: 80, sorter: (a, b) => (a.signed/a.total) - (b.signed/b.total),
      render: (v, r) => {
        const pct = v > 0 ? (r.signed / v * 100).toFixed(1) : 0;
        return <span style={{ color: pct >= 80 ? '#52c41a' : pct >= 50 ? '#faad14' : '#ff4d4f' }}>{pct}%</span>;
      }},
  ];

  const monthCols = [
    { title: '月份', dataIndex: 'month', width: 100 },
    { title: '总数', dataIndex: 'total', width: 80 },
    { title: '已签', dataIndex: 'signed', width: 80, render: v => <span style={{ color: '#52c41a' }}>{v}</span> },
    { title: '未签', dataIndex: 'unsigned', width: 80, render: v => <span style={{ color: v > 0 ? '#ff4d4f' : '#999' }}>{v}</span> },
  ];

  if (loading || !data) return <Spin style={{ display: 'block', margin: '30vh auto' }} size="large" />;

  return (
    <div>
      <h3 style={{ marginBottom: 16 }}>签收统计看板</h3>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card><Statistic title="总发货单" value={safeKpi.total} prefix={<ClockCircleOutlined />} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="已签收" value={safeKpi.signed} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="未签收" value={safeKpi.unsigned} valueStyle={{ color: '#ff4d4f' }} prefix={<WarningOutlined />} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="签收率" value={safeKpi.rate} suffix="%" valueStyle={{ color: safeKpi.rate >= 80 ? '#52c41a' : '#ff4d4f' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="超12天未签" value={safeKpi.over6days} valueStyle={{ color: '#faad14' }} prefix={<WarningOutlined />} /></Card>
        </Col>
        <Col span={4}>
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button size="small" icon={<DownloadOutlined />} onClick={() => handleExport('')}>全部导出</Button>
              <Button size="small" onClick={() => handleExport('unsigned')}>未签导出</Button>
              <Button size="small" onClick={() => handleExport('over6days')}>超12天导出</Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card title="签收方式">
            <Table dataSource={safeSignTypes} rowKey="type" size="small" pagination={false}
              columns={[
                { title: '方式', dataIndex: 'type', render: v => v === 'asign' ? <Tag color="blue">爱签</Tag> : <Tag>离线</Tag> },
                { title: '数量', dataIndex: 'count' },
              ]} />
          </Card>
        </Col>
        <Col span={16}>
          <Card title="月度趋势">
            <Table dataSource={safeMonths} rowKey="month" size="small" pagination={false} columns={monthCols} />
          </Card>
        </Col>
      </Row>

      <Card title="经销商签收明细" extra={
        <Input placeholder="筛选经销商" allowClear style={{ width: 200 }}
          value={dealerFilter} onChange={e => setDealerFilter(e.target.value)} />
      }>
        <Table dataSource={filteredDealers} rowKey="name" size="small" pagination={{ pageSize: 30 }} columns={dealerCols} />
      </Card>
    </div>
  );
}
